/**
 * The settings form's items in keyboard order, and what each does when stepped (left / right,
 * wheel, arrows), activated (Enter, click) or cleared (Delete, right click).
 */

import { COLOUR_NAMES } from '../../render/palette';
import type { ColourSlot, FieldId } from '../model/fields';
import {
  COLOUR_SLOTS, cycleColour, fillTotal, NUMBER_FIELDS, nudge, nudgeFillPercent, removeFillItem, setField, setFillBase,
  setFillItem,
} from '../model/fields';
import { MAX_FILL_ITEMS } from '../model/doc';
import { canFill, elementName, KIT_ELEMENTS } from '../model/palette';
import type { TextField } from '../model/text';
import type { KitApi } from './api';

export interface FormItem {
  readonly id: string;
  tip(k: KitApi): string;
  /** Left / right, wheel, the arrows: -1 or +1. */
  step(k: KitApi, dir: number): void;
  /** Enter, or a click on the value. */
  activate?(k: KitApi): void;
  /** Delete, or a right click. */
  clear?(k: KitApi): void;
}

function textItem(field: TextField, tip: string): FormItem {
  return {
    id: field,
    tip: () => tip,
    step: () => {},
    activate: (k) => {
      k.editing = field;
      k.sound('select');
    },
  };
}

function numberItem(id: FieldId): FormItem {
  const f = NUMBER_FIELDS[id];
  return {
    id,
    tip: () => `${f.tip} (${f.min}-${f.max})`,
    step: (k, dir) => {
      if (k.st.edit(nudge(k.st.doc, id, dir * (k.big() ? f.big : f.step)), id)) k.sound('move');
    },
    ...(id === 'seed'
      ? { activate: (k: KitApi) => void k.st.edit(setField(k.st.doc, 'seed', 1 + Math.floor(Math.random() * 99999))) }
      : {}),
  };
}

function colourItem(slot: ColourSlot): FormItem {
  const s = COLOUR_SLOTS[slot];
  return {
    id: `c${slot}`,
    tip: (k) => `${s?.tip ?? ''}: ${COLOUR_NAMES[k.st.doc.colors[slot]] ?? '?'}`,
    step: (k, dir) => {
      if (k.st.edit(cycleColour(k.st.doc, slot, dir < 0 ? -1 : 1), `c${slot}`)) k.sound('move');
    },
  };
}

const FILLABLE = KIT_ELEMENTS.filter((e) => canFill(e.ch)).map((e) => e.ch);

/** Refuse (with a reason) an element the fill cannot use. */
function fillable(k: KitApi): boolean {
  if (canFill(k.element)) return true;
  k.st.say(`THE RANDOM FILL CANNOT MAKE ${elementName(k.element)}`);
  k.sound('error');
  return false;
}

const fillBase: FormItem = {
  id: 'fillBase',
  tip: (k) => `? CELLS ARE ${elementName(k.st.doc.fill.base)} UNLESS AN ITEM ROLLS. CLICK: THE SELECTED ELEMENT`,
  step: (k, dir) => {
    const i = FILLABLE.indexOf(k.st.doc.fill.base);
    const next = FILLABLE[(i + dir + FILLABLE.length) % FILLABLE.length] ?? '.';
    if (k.st.edit(setFillBase(k.st.doc, next))) k.sound('move');
  },
  activate: (k) => {
    if (fillable(k)) k.st.edit(setFillBase(k.st.doc, k.element));
  },
};

function fillItem(i: number): FormItem {
  return {
    id: `fill${i}`,
    tip: (k) => {
      const it = k.st.doc.fill.items[i];
      if (!it) return 'ADD A RANDOM ITEM: CLICK TO ADD THE SELECTED ELEMENT';
      const total = fillTotal(k.st.doc.fill);
      return `${elementName(it[0])}: ${it[1]}% OF ? CELLS (ITEMS ${total}%). CLICK: SET TO SELECTED, RIGHT: REMOVE`;
    },
    step: (k, dir) => {
      if (k.st.edit(nudgeFillPercent(k.st.doc, i, dir * (k.big() ? 5 : 1)), `fill${i}`)) k.sound('move');
    },
    activate: (k) => {
      if (i > k.st.doc.fill.items.length || i >= MAX_FILL_ITEMS || !fillable(k)) return;
      k.st.edit(setFillItem(k.st.doc, i, k.element));
    },
    clear: (k) => void k.st.edit(removeFillItem(k.st.doc, i)),
  };
}

export const FORM_ITEMS: readonly FormItem[] = [
  textItem('name', 'THE CAVE NAME: UP TO 16 OF A-Z 0-9 SPACE . ! \' -  (CLICK OR ENTER TO TYPE)'),
  numberItem('width'),
  numberItem('height'),
  textItem('hint', 'ONE LINE SHOWN BEFORE THE CAVE, UP TO 60 CHARACTERS (CLICK OR ENTER TO TYPE)'),
  numberItem('diamonds'),
  numberItem('time'),
  numberItem('tick'),
  numberItem('seed'),
  numberItem('diamondValue'),
  numberItem('extraValue'),
  numberItem('amoebaTime'),
  numberItem('magicWallTime'),
  colourItem(0),
  colourItem(1),
  colourItem(2),
  fillBase,
  ...Array.from({ length: MAX_FILL_ITEMS }, (_, i) => fillItem(i)),
];

export function formIndexOf(id: string): number {
  return FORM_ITEMS.findIndex((f) => f.id === id);
}
